import { PERMISSION_KEYS, type PermissionKey, type PermissionSource } from '@servicetim/shared';
import { useEffectivePermissions, useProjectRoles } from '@servicetim/ui/api';
import {
  AsyncPicker,
  LoadingState,
  ProblemBanner,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { cn, useSessionClaims } from '@servicetim/ui/lib';
import { Check, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { FormRow } from '../ui/form-row';
import { Section } from '../ui/section';

/**
 * Compact effective-permissions inspector (S-03-05): pick a user, see every permission key with
 * where it comes from. Stands in until G2's `features/access` inspector is available; the page
 * can swap the import without other changes.
 */
export function EffectivePermissionsCard({ projectId }: { projectId: string }) {
  const id = useId();
  const { userId: me } = useSessionClaims();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const effective = useEffectivePermissions(projectId, userId);
  const roles = useProjectRoles(projectId);
  const loadUsers = useUserOptionLoader();
  const roleNames = useMemo(
    () => new Map((roles.data ?? []).map((r) => [r.id, r.name])),
    [roles.data],
  );
  const granted = new Set(effective.data?.permissions ?? []);

  const describeSource = (s: PermissionSource): string => {
    switch (s.via) {
      case 'tenant_role':
        return `tenant role ${s.tenantRole ?? ''}`.trim();
      case 'project_role':
        return `role ${roleNames.get(s.roleId ?? '') ?? s.roleId ?? ''}`;
      case 'group':
      case 'group_grant':
        return `group ${s.groupId?.slice(0, 8) ?? ''}… ${s.roleId ? `as ${roleNames.get(s.roleId) ?? s.roleId}` : ''}`.trim();
      case 'user':
        return 'direct user grant';
      case 'dynamic':
        return `dynamic (${s.grantee ?? ''})`;
      default:
        return s.via;
    }
  };

  return (
    <Section
      title="Effective permissions"
      description="Why can (or cannot) someone do something here: every permission with its source."
    >
      <FormRow
        label="User"
        htmlFor={`${id}-user`}
        hint={userId ? undefined : 'Showing your own permissions.'}
        className="max-w-sm"
      >
        <AsyncPicker
          id={`${id}-user`}
          value={userId}
          onChange={setUserId}
          load={loadUsers}
          displayLabel={userId === me ? 'You' : undefined}
          placeholder="You"
        />
      </FormRow>
      {effective.isPending ? (
        <LoadingState rows={3} />
      ) : effective.error ? (
        <ProblemBanner error={effective.error} size="sm" onRetry={() => void effective.refetch()} />
      ) : (
        <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {PERMISSION_KEYS.map((key: PermissionKey) => {
            const has = granted.has(key);
            const sources = effective.data?.sources[key] ?? [];
            return (
              <li
                key={key}
                className={cn(
                  'flex items-start gap-2 rounded-md border px-2 py-1.5 text-sm',
                  has
                    ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/10'
                    : 'border-border bg-surface-sunken/50',
                )}
              >
                {has ? (
                  <Check
                    className="mt-0.5 size-4 text-emerald-600 dark:text-emerald-400"
                    aria-label="granted"
                  />
                ) : (
                  <X className="mt-0.5 size-4 text-fg-subtle" aria-label="not granted" />
                )}
                <span className="min-w-0">
                  <code className="block font-mono text-xs">{key}</code>
                  {sources.length > 0 ? (
                    <span className="block truncate text-fg-muted text-xs">
                      via {sources.map(describeSource).join(', ')}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
